import { createContext, useContext, ReactNode } from "react";
import { User, UserRole } from "../generated/types";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { useSignOut } from "../hooks/useSignOut";

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isClient: boolean;
  forcePasswordChange: boolean;
  refetch: () => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { user, loading, refetch, isAuthenticated } = useCurrentUser();
  const { signOut: signOutMutation } = useSignOut();

  const value: AuthContextType = {
    currentUser: user,
    loading,
    isAuthenticated,
    isAdmin: user?.role === UserRole.Admin,
    isClient: user?.role === UserRole.Client,
    forcePasswordChange: user?.forcePasswordChange || false,
    refetch,
    signOut: async () => {
      await signOutMutation();
      await refetch();
    }
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
